import { FC, useCallback, useState } from "react";

import Loading from "web/Layout/Common/Loading";
import __ from "web/Layout/Translations";

import useInternetConnection from "web/hooks/useInternetConnection/useInternetConnection";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import {
  IProductCodeLine,
  useGetBarCodeQuery,
  useGetCodeSettingsQuery,
  useGetProductCodeQuery,
} from "web/features/codes/codesApiSlice";

import CodeLine from "../CodeLine";
import DownloadCodeButton from "./DownloadCodeButton";
import classes from "./codeModal.scss";
import printCode from "./utils/printCode";
import printCodeDirectly from "./utils/printCodeDirectly";

interface ICodeModalContainerProps {
  productName: string;
  itemId: number;
  orderPaid: boolean;
  sku: string;
}

const CodeModalContainer: FC<ICodeModalContainerProps> = ({
  productName,
  itemId,
  orderPaid,
  sku,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const { data: codeSettingsData, isLoading: loading } =
    useGetCodeSettingsQuery(sku);
  const canPrintCode = !!codeSettingsData?.canPrintCode;

  const { data: codeData } = useGetProductCodeQuery(itemId, {
    skip: !orderPaid,
  });
  const { code, isSplitted, isNewStructure } = codeData || {};

  const { data: barcode } = useGetBarCodeQuery(itemId, {
    skip: !orderPaid || !canPrintCode,
  });

  const { isDisconnectedWebView } = useInternetConnection();

  const print = useCallback(
    () => printCode({ itemId, setIsPrinting }),
    [itemId]
  );

  // eslint-disable-next-line no-underscore-dangle
  const _code =
    (isSplitted
      ? (code?.[0] as IProductCodeLine)?.value ?? code?.[0]
      : (code as string)) ?? null;

  const onPrintCodeDirectlyHandler = () =>
    printCodeDirectly({ productName, code: _code });

  const codeLineClasses = {
    codeLine: classes.codeLine,
    codeLineName: classes.codeLineName,
    codeLineValue: classes.codeLineValue,
    codeLineCopyButton: classes.codeLineCopyButton,
    codeLineCopyIcon: classes.codeLineCopyIcon,
  };

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.buttons}>
          <DownloadCodeButton
            hasCode={!!code}
            canPrintCode={canPrintCode}
            isPrinting={isPrinting}
            onPrintHandler={print}
            onPrintCodeDirectlyHandler={onPrintCodeDirectlyHandler}
          />
        </div>
        <div className={classes.title} data-t1="code_modal_title">
          <h2 className={classes.header} data-t1="code_modal_header">
            {__("Twój kod")}
          </h2>
          <h2 className={classes.productName} data-t1="code_modal_product_name">
            {productName}
          </h2>
        </div>
        <div className={classes.box}>
          {loading ? (
            <Loading />
          ) : (
            <div className={classes.codeContainer}>
              {barcode && canPrintCode ? (
                <div className={classes.codeImageContainer}>
                  <img
                    className={classes.codeImage}
                    src={barcode}
                    alt=""
                  />
                </div>
              ) : null}
              {code && (
                <div className={classes.codeLines}>
                  {isSplitted && isArrayHasItems(code) ? (
                    code.map((line) =>
                      isNewStructure ? (
                        <CodeLine
                          key={(line as IProductCodeLine)?.name}
                          label={(line as IProductCodeLine)?.name}
                          value={(line as IProductCodeLine)?.value}
                          dataT1="code_modal_code"
                          classes={codeLineClasses}
                        />
                      ) : (
                        <CodeLine
                          key={line as string}
                          className={classes.codeLine}
                          data-t1="code_modal_code"
                          value={line as string}
                          classes={codeLineClasses}
                        />
                      )
                    )
                  ) : (
                    <CodeLine
                      key="code"
                      value={code as string}
                      dataT1="code_modal_code"
                      classes={codeLineClasses}
                    />
                  )}
                </div>
              )}
              {isDisconnectedWebView && (!barcode || !code) && (
                <p>{__("Brak połączenia z siecią")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeModalContainer;
